import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { EnvironmentService } from '../../singleton-services/environment.service'
import { noop } from '../../shared/functions/noop'
import { debounceTime, mergeMap, take, takeUntil } from 'rxjs/operators'
import { VideoPlayerMeta } from '../models/stream-tile'
import { Subject } from 'rxjs'

export interface IVideoLoggingContext {
  /**
   * Unique GUID for a single live experience (equates to a unique key per browser tab)
   */
  readonly playerId: string

  /**
   * Unique GUID of the video player instance (<video> element)
   * Since 2 - n players can exists on a single screen, we need to determine which player error runs to
   */
  readonly playerInstance: string

  /**
   * EX: http://flosportshls-i.akamaihd.net/hls/live/593115/event/wrp252310_m19/playlist.m3u8?hdnea:st=1516...1d61a99e0563144eb6e
   */
  readonly file: string

  /**
   * URL where video is started
   */
  readonly referer: string

  /**
   * Name of the app making requests. This is to differentiate between multiple apps using the API
   */
  readonly appName: string

  /**
   * App version
   */
  readonly appVersion: string

  /**
   * Primary video player plugin
   */
  readonly pluginName: string

  /**
   * Primary video player plugin version
   */
  readonly pluginVersion: string

  /** Stream Id */
  readonly streamId: string

  /** Event Status [Live, Concluded, Etc.] */
  readonly eventStatus: string
}

export interface IVideoLoggingExtendedContext<TValue = any> extends IVideoLoggingContext {
  value: TValue
  message?: string
}

export interface IVideoLoggingUser {
  id: number
  resolution: string
}

export interface IVideoLoggingPreviousStateValueType {
  previous?: string
}

export interface IVideoLoggingVolumeValueType {
  volume: number
}

export interface IVideoLoggingErrorValueType {
  code: number
}

export interface IVideoLoggingMuteValueType {
  mute: boolean
}

export interface IVideoLoggingInfoValue {
  videoReferenceId: string
  isFreeStream: boolean
  videoMeta: VideoPlayerMeta
}

export interface IVideoLoggingSeekValueType {
  position: number
  offset: number
}

export interface IVideoLoggingVisualQualityValueType {
  bitrate: number
  width: number
  height: number
  label: string
  automatic: boolean
}

export interface IVideoLoggingPostBody {
  type: LogEventType
  context: IVideoLoggingContext & { error?: string }
  user: IVideoLoggingUser
}

export enum LogEventType {
  AD = 'ad',
  READY = 'ready',
  PLAY = 'play',
  PAUSE = 'pause',
  BUFFER = 'buffer',
  VISUAL_QUALITY = 'visualquality',
  SEEK = 'seek',
  VOLUME = 'volume',
  MUTE = 'mute',
  ERROR = 'error',
  INFO = 'info'
}
interface PostLogData {
  tokenId: string
  context: IVideoLoggingExtendedContext<IVideoLoggingVolumeValueType>
  user: IVideoLoggingUser
  logEventType: LogEventType
}
@Injectable()
export class VideoEventLoggingService implements OnDestroy {
  constructor(private readonly http: HttpClient, private readonly env: EnvironmentService) {}

  private readonly ngOnDestroy$ = new Subject<void>()

  public readonly logVolumeEvent = new Subject<PostLogData>()
  private readonly logVolumeEvent$ = this.logVolumeEvent.asObservable()

  /**
   * When the user slides the volume slider, the volume change event is triggered for each volume change
   *  ex. changing from 0-100 will log: 0,1,2,3,4,... 100
   * A debounce is added in the http post for volume in order to provide more meaningful logs when changing the volume
   */
  postVolumeLog$ = this.logVolumeEvent$
    .pipe(
      debounceTime(500),
      mergeMap(({ tokenId, context, user, logEventType }) =>
        this.http.post(`${this.env.config.endpoints.loggingApi}/tokens/${tokenId}/logs`, {
          type: logEventType,
          context,
          user
        })
      ),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(noop, noop)

  private readonly post = (tokenId: string, body: IVideoLoggingPostBody, path: string) => {
    this.http
      .post(`${this.env.config.endpoints.loggingApi}/tokens/${tokenId}/${path}`, body)
      .pipe(take(1))
      .subscribe(noop, noop)
  }

  private readonly postLog = (tokenId: string, body: IVideoLoggingPostBody) => this.post(tokenId, body, 'logs')
  private readonly postFail = (tokenId: string, body: IVideoLoggingPostBody) => this.post(tokenId, body, 'failures')
  public readonly logAdEvent = (tokenId: string, log: IVideoLoggingPostBody) => this.postLog(tokenId, log)
  public readonly logVideoErrorEvent = (tokenId: string, log: IVideoLoggingPostBody) => this.postFail(tokenId, log)

  private readonly postBase = (
    tokenId: string,
    context: IVideoLoggingContext,
    user: IVideoLoggingUser,
    type: LogEventType
  ) => this.postLog(tokenId, { type, context, user })

  public readonly logReadyEvent = (tokenId: string, context: IVideoLoggingContext, user: IVideoLoggingUser) =>
    this.postBase(tokenId, context, user, LogEventType.READY)

  public readonly logPlayEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingPreviousStateValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.PLAY)

  public readonly logPauseEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingPreviousStateValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.PAUSE)

  public readonly logBufferEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingPreviousStateValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.BUFFER)

  public readonly logVisualQualityEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingVisualQualityValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.VISUAL_QUALITY)

  public readonly logSeekEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingSeekValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.SEEK)

  public readonly logMuteEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingMuteValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.MUTE)

  public readonly logInfoEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingInfoValue>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.INFO)

  public readonly logErrorEvent = (
    tokenId: string,
    context: IVideoLoggingExtendedContext<IVideoLoggingErrorValueType>,
    user: IVideoLoggingUser
  ) => this.postBase(tokenId, context, user, LogEventType.ERROR)

  ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
